import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../Modal';
import Spinner from '../../Spinner';
import { RSAlert, RSButton } from 'reactsymbols-kit';
import { saveAs } from 'file-saver';
import Styles from '../../../styles/colors';
import type { IModelVersion } from '../../../stores/projects/model_version';

/** Props for the inner {@link ExportModel} content and its modal wrapper. */
interface ExportModelModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** The version to export to MXP. */
  modelVersion: IModelVersion | null | undefined;
  /** The model name used for the saved file. */
  modelName: string;
  /** Close the modal. */
  onRequestClose: () => void;
}

/**
 * Export the model
 */
function ExportModel({ modelVersion, onRequestClose, modelName }: ExportModelModalProps) {
  const [exporting, setExporting] = useState(true);
  const [error, setError] = useState(false);

  // Keep the latest props in refs so exportModel (used by the mount effect and
  // the retry button) always sees fresh values without re-registering.
  const propsRef = useRef({ modelVersion, onRequestClose, modelName });
  propsRef.current = { modelVersion, onRequestClose, modelName };

  // export the model and then save the result
  const exportModel = () => {
    const { modelVersion, onRequestClose, modelName } = propsRef.current;

    if (modelVersion) {
      setExporting(true);
      setError(false);
      modelVersion
        .export()
        .then((blob: Blob) => {
          saveAs(blob, `${modelName}.mxp`);
          onRequestClose();
        })
        .catch(() => {
          setExporting(false);
          setError(true);
        });
    } else {
      setExporting(false);
      setError(true);
    }
  };

  // start exporting on component load (componentDidMount)
  useEffect(() => {
    exportModel();
     
  }, []);

  // render either a spinner or an error
  const renderContent = () => {
    if (exporting) {
      return (
        <React.Fragment>
          <Spinner />
          Please be patient while we export your model.
        </React.Fragment>
      );
    } else if (error) {
      return (
        <React.Fragment>
          <RSAlert level="danger" visible={true}>
            <p>We couldn't export your model because of an unexpected error.</p>
          </RSAlert>
          <div className="button-wrapper">
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              onClick={exportModel}
              value="Retry"
            />
            <RSButton
              value="Close"
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              onClick={onRequestClose}
            />
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  };

  return <div className="export-model-modal">{renderContent()}</div>;
}

// Wrap in a Modal
const ExportModelModal = (props: ExportModelModalProps) => (
  <Modal isOpen={props.isOpen}>
    <ExportModel {...props} />
  </Modal>
);

export default ExportModelModal;
